import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Grid, Group, RingProgress, Stack, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertTriangle,
  Award,
  CheckCircle2,
  Circle,
  Clock,
  FlaskConical,
  Layers,
  Loader,
  MonitorPlay,
  Play,
  Square,
  TerminalSquare,
  Wrench,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, ConfirmDialog, ErrorState, Pill, Skeleton } from '../components/ui';
import { PageContainer, PageHeader, Section } from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { LabRunner } from '../components/lab/LabRunner';
import { categoryLabel, DIFFICULTY_META, toolLabel } from '../components/lab/labMeta';
import { useLab, useStartLab, useStopLab, useExtendLab } from '../hooks/useLabs';
import { useLabProgress } from '../hooks/useLabProgress';
import { useNow } from '../hooks/useNow';
import type { LabTaskProgress, LabTaskStatus } from '../api/types';
import { colors } from '../theme/tokens';

const STATUS_VARIANT: Record<LabTaskStatus, 'success' | 'info' | 'warn' | 'danger'> = {
  completed: 'success',
  in_progress: 'info',
  failed: 'danger',
  not_started: 'warn',
};

const STATUS_LABEL: Record<LabTaskStatus, string> = {
  completed: 'Completed',
  in_progress: 'In progress',
  failed: 'Failed',
  not_started: 'Not started',
};

export const Route = createFileRoute('/labs/$id')({
  component: LabDetail,
});

function LabDetail() {
  const { id } = Route.useParams();
  const { data, isPending, isError, error } = useLab(id);
  const progressQuery = useLabProgress(id);
  const startLab = useStartLab();
  const stopLab = useStopLab();
  const extendLab = useExtendLab();

  // Prefer the POLLED progress status (it advances provisioning→running→ready);
  // fall back to the start-call response only until the first poll lands.
  const liveStatus = progressQuery.data?.status ?? startLab.data?.status;
  // Enrollment id for task submission — from the start click or (after refresh)
  // the polled progress.
  const enrollmentId =
    startLab.data?.enrollment_id ?? progressQuery.data?.enrollmentId ?? undefined;
  const isProvisioning = liveStatus === 'provisioning';
  const isActive = liveStatus === 'running' || liveStatus === 'ready';
  const expiresAt = progressQuery.data?.expiresAt ?? null;
  // Ticking clock — computed inline this used to freeze at mount, so the
  // "Xm left" badge lied for the whole session.
  const now = useNow();
  const minsLeft = expiresAt
    ? Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 60000))
    : null;
  const [confirmStop, setConfirmStop] = useState(false);
  const extendsRemaining = Math.max(
    0,
    (progressQuery.data?.maxExtends ?? 2) - (progressQuery.data?.extendCount ?? 0),
  );
  // One-lab-per-user: the start endpoint 409s when another lab is already running.
  const startErr = startLab.error as { status?: number; body?: { message?: string; reason?: string } } | null;
  const startConflict =
    startErr?.status === 409
      ? startErr.body?.message ?? startErr.body?.reason ?? 'You already have a lab running. Stop it first.'
      : null;
  // Open the lab desktop (Kibana/tools) — reuses the range console at
  // /console/{rangeId}/lab-vm. Enabled once the lab is ready (VM up + xrdp).
  const navigate = useNavigate();
  const rangeId = progressQuery.data?.rangeId;
  // Open desktop ONLY once the lab is fully ready (tools booted — Kibana on :5601
  // etc. actually up). During 'running' the VM is reachable but the tools are
  // still starting, so opening then shows an empty tool — gate the button on
  // 'ready' and surface a "tools starting…" hint instead.
  const canOpenDesktop = liveStatus === 'ready' && !!rangeId;
  const toolsStarting = liveStatus === 'running';
  const openDesktop = () => {
    if (rangeId)
      navigate({
        to: '/console/$rangeId/$vmName',
        params: { rangeId: String(rangeId), vmName: 'lab-vm' },
        search: { returnTo: `/labs/${id}` },
      });
  };

  // Bulletproof polling: refetchInterval alone doesn't reliably restart after a
  // start, so while the lab is coming up (started but not yet ready) we refetch
  // progress on a fixed interval — the running controls then appear on their own,
  // no manual refresh needed.
  const isTerminal =
    liveStatus === 'ready' || liveStatus === 'stopped' || liveStatus === 'completed' || liveStatus === 'not_started';
  // Keep the backup poll alive for the whole non-terminal window — including a
  // transient 'unknown' after a page refresh (when startLab.isSuccess is false).
  const isComingUp = startLab.isSuccess || (!!liveStatus && !isTerminal);
  useEffect(() => {
    if (!isComingUp || isTerminal) return;
    const t = window.setInterval(() => void progressQuery.refetch(), 6000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComingUp, isTerminal]);

  // Index per-task progress by taskId for O(1) lookup in the task list.
  // useMemo per Performance skill — labs with 10+ tasks (apt-full-spectrum
  // has 7 today, ransomware-containment 7, but the cap is unbounded).
  const progressByTaskId = useMemo(() => {
    const map = new Map<string, LabTaskProgress>();
    if (progressQuery.data) {
      for (const t of progressQuery.data.tasks) map.set(t.taskId, t);
    }
    return map;
  }, [progressQuery.data]);

  if (isPending) {
    return (
      <PageContainer size="default">
        <Stack gap="lg">
          <Skeleton height={48} width="40%" />
          <Skeleton height={120} radius="md" />
          <Skeleton lines={5} height={20} gap={12} />
        </Stack>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer size="default">
        <ErrorState title="Could not load lab" error={error} />
      </PageContainer>
    );
  }

  const progress = progressQuery.data;
  const completed = progress?.tasks.filter((t) => t.status === 'completed').length ?? 0;
  const percent = progress?.percentComplete ?? 0;

  const diff = DIFFICULTY_META[data.difficulty];

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={FlaskConical}
        title={data.title}
        description={`${diff.label} · ${categoryLabel(data.category)} · ${toolLabel(data.amiRole)}`}
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Labs', to: '/labs' }, { label: 'Detail' }]}
        actions={
          isActive ? (
            <Group gap="xs">
              <Button
                variant="primary"
                size="sm"
                leftIcon={<TerminalSquare size={14} />}
                disabled={!canOpenDesktop}
                onClick={openDesktop}
              >
                {canOpenDesktop ? 'Open desktop' : 'Preparing…'}
              </Button>
              {toolsStarting ? (
                <Badge variant="warn">tools starting…</Badge>
              ) : null}
              {minsLeft !== null ? (
                <Badge variant={minsLeft <= 5 ? 'danger' : 'info'}>
                  <Clock size={12} /> {minsLeft}m left
                </Badge>
              ) : null}
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Clock size={14} />}
                loading={extendLab.isPending}
                // Extend only makes sense once the 1h clock has started (expiresAt
                // set at 'ready'); the backend rejects it earlier too.
                disabled={extendsRemaining <= 0 || expiresAt === null}
                onClick={() => extendLab.mutate({ slug: id })}
              >
                {extendsRemaining > 0 ? `Extend +30m (${extendsRemaining} left)` : 'No extends left'}
              </Button>
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Square size={14} />}
                loading={stopLab.isPending}
                onClick={() => setConfirmStop(true)}
              >
                Stop lab
              </Button>
            </Group>
          ) : (
            <Button
              variant="primary"
              leftIcon={<Play size={14} />}
              loading={startLab.isPending}
              disabled={startLab.isPending || isProvisioning}
              onClick={() => startLab.mutate({ slug: id })}
            >
              {startLab.isPending ? 'Starting…' : isProvisioning ? 'Provisioning…' : 'Start lab'}
            </Button>
          )
        }
      />

      {startConflict ? (
        <Alert color="orange" icon={<AlertTriangle size={16} />} variant="light" mb="md">
          {startConflict}
        </Alert>
      ) : null}

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Card mb="md">
            <Stack gap="sm">
              <Group gap={6} wrap="wrap">
                <Badge variant={diff.variant}>{diff.label}</Badge>
                <Badge variant="neutral">{categoryLabel(data.category)}</Badge>
                <Badge variant="info">{toolLabel(data.amiRole)}</Badge>
              </Group>
              <Text c="dimmed" size="sm">{data.description}</Text>
            </Stack>
          </Card>

          {isActive && progress?.access ? (
            <Card mb="md" style={{ borderColor: 'var(--c-bd-bright)' }}>
              <Stack gap="xs">
                <Group gap="xs">
                  <MonitorPlay size={16} color={colors.neon} />
                  <Text fw={600} size="sm">How to access this lab</Text>
                  {progress.access.app ? <Badge variant="info" size="xs">{progress.access.app}</Badge> : null}
                </Group>
                <Text size="sm" c="dimmed">{progress.access.how}</Text>
                {progress.access.url ? (
                  <Text size="sm">Inside the desktop, browse to <strong className="font-mono">{progress.access.url}</strong></Text>
                ) : null}
              </Stack>
            </Card>
          ) : null}

          <Section title="Tasks" description="Work through each step and submit your answer — progress tracks here.">
            <motion.div variants={staggerContainer} initial="hidden" animate="show" className="flex flex-col gap-3">
              {data.tasks.map((task, i) => {
                const taskProgress = progressByTaskId.get(task.id);
                const st: LabTaskStatus = taskProgress?.status ?? 'not_started';
                return (
                  <motion.div key={task.id} variants={cardItem}>
                    <Card style={st === 'completed' ? { borderColor: 'rgb(var(--c-hud-green))' } : undefined}>
                      <Group gap="sm" wrap="nowrap" align="start">
                        <StepNumber index={i + 1} status={st} />
                        <Stack gap={8} style={{ flex: 1, minWidth: 0 }}>
                          <Group justify="space-between" wrap="nowrap" gap="xs">
                            <Text fw={600} size="sm" c="rgb(var(--c-txt-bright))">{task.title}</Text>
                            <Group gap={8} wrap="nowrap">
                              {task.points ? <Badge variant="neutral" size="xs">{task.points} pts</Badge> : null}
                              <Pill variant={STATUS_VARIANT[st]} size="xs"><StatusIcon status={st} /> {STATUS_LABEL[st]}</Pill>
                            </Group>
                          </Group>
                          <LabRunner
                            labId={data.id}
                            task={task}
                            index={i + 1}
                            enrollmentId={enrollmentId}
                            progress={taskProgress}
                            policy={progressQuery.data?.policy}
                          />
                        </Stack>
                      </Group>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          </Section>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 4 }}>
          <div style={{ position: 'sticky', top: 16 }}>
            <Stack gap="md">
              <Card>
                <Stack gap="sm" align="center">
                  <RingProgress
                    size={132}
                    thickness={10}
                    roundCaps
                    sections={[{ value: percent, color: 'teal' }]}
                    label={
                      <Stack gap={0} align="center">
                        <Text fw={700} size="xl">{percent}%</Text>
                        <Text size="xs" c="dimmed">complete</Text>
                      </Stack>
                    }
                  />
                  <Group gap="xl">
                    <SideStat icon={<CheckCircle2 size={14} />} label="Tasks" value={`${completed}/${data.taskCount}`} />
                    {data.points ? <SideStat icon={<Award size={14} />} label="Points" value={String(data.points)} /> : null}
                  </Group>
                  {liveStatus ? (
                    <Badge variant={isActive ? 'success' : isProvisioning ? 'info' : 'neutral'}>
                      {isActive ? 'Running' : isProvisioning ? 'Provisioning' : liveStatus}
                    </Badge>
                  ) : null}
                </Stack>
              </Card>
              <Card>
                <Stack gap="sm">
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">Lab facts</Text>
                  <SideFact icon={<Activity size={14} />} label="Difficulty" value={diff.label} />
                  <SideFact icon={<Layers size={14} />} label="Category" value={categoryLabel(data.category)} />
                  <SideFact icon={<Wrench size={14} />} label="Tool" value={toolLabel(data.amiRole)} />
                  <SideFact icon={<Clock size={14} />} label="Est. time" value={`${data.estimatedMins}m`} />
                </Stack>
              </Card>
            </Stack>
          </div>
        </Grid.Col>
      </Grid>

      {/* Stopping destroys the lab VM — it used to fire on a single click. */}
      <ConfirmDialog
        opened={confirmStop}
        onClose={() => setConfirmStop(false)}
        onConfirm={() => {
          stopLab.mutate({ slug: id });
          setConfirmStop(false);
        }}
        title="Stop this lab?"
        confirmLabel="Stop lab"
        loading={stopLab.isPending}
      >
        {`"${data.title}" will be torn down and its VM destroyed. Submitted answers are kept; anything unsaved inside the desktop is lost.`}
      </ConfirmDialog>
    </PageContainer>
  );
}

function StepNumber({ index, status }: { index: number; status: LabTaskStatus }) {
  const done = status === 'completed';
  return (
    <div
      style={{
        flexShrink: 0,
        width: 30,
        height: 30,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 13,
        color: done ? '#03130a' : 'rgb(var(--c-txt-bright))',
        background: done ? 'rgb(var(--c-hud-green))' : 'rgb(var(--c-card-2, var(--c-card)))',
        border: '1px solid var(--c-bd-bright)',
      }}
    >
      {done ? <CheckCircle2 size={16} /> : index}
    </div>
  );
}

function SideStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Stack gap={0} align="center">
      <Group gap={4}>
        <span style={{ color: colors.hudCyan }}>{icon}</span>
        <Text className="font-mono" fw={700} c="rgb(var(--c-txt-bright))">{value}</Text>
      </Group>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
    </Stack>
  );
}

function SideFact({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Group gap={8}>
        <span style={{ color: colors.hudCyan }}>{icon}</span>
        <Text size="sm" c="dimmed">{label}</Text>
      </Group>
      <Text size="sm" fw={600} c="rgb(var(--c-txt-bright))">{value}</Text>
    </Group>
  );
}

function StatusIcon({ status }: { status: LabTaskStatus }) {
  if (status === 'completed') return <CheckCircle2 size={12} />;
  if (status === 'in_progress') return <Loader size={12} />;
  if (status === 'failed') return <XCircle size={12} />;
  return <Circle size={12} />;
}
